import { TYPE_TO_READABLE_STRING_MAP, MENU_OBJECT_TYPES } from 'constants/menuManagement';
import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';
import { normalizeServingsTag } from 'components/MenuManagement/validation';
import {
  diffChangedFields,
  EDIT_DRAWER_TRACKED_FIELDS,
  toAnalyticsEntityType,
  trackMenuEntityAction,
} from 'helpers/menuAnalytics';
import { CreateSubmitHandlerParams, EditDrawerFormValues } from '../types';

function getReadableItemType(itemType: string | undefined): string {
  if (!itemType) {
    return 'item';
  }

  const entry = Object.entries(TYPE_TO_READABLE_STRING_MAP).find(([key]) => key === itemType);
  return entry?.[1]?.toLowerCase() || 'item';
}

// Mirrors the parent field label rendered by menuManagementColumns so the
// error message matches the field's visible label.
function getParentRequiredMessage(itemType: string | undefined, isNested: boolean): string {
  if (itemType === MENU_OBJECT_TYPES.items) {
    return 'Category is required.';
  }
  if (itemType === MENU_OBJECT_TYPES.modifiers) {
    return 'Modifier group is required.';
  }
  if (itemType === MENU_OBJECT_TYPES.modifierGroups) {
    return isNested ? 'Parent Modifier is required.' : 'Item is required.';
  }
  return 'Item is required.';
}

// IMPORTANT: 0 is a valid tax rate and must be sent as 0.
// The backend interprets 0 as non-taxable, while an empty string means "no override".
function getTaxRate(
  taxRate: string | { taxRate: string | number } | number | undefined,
): number | '' {
  if (typeof taxRate === 'object' && taxRate !== null) {
    const { taxRate: innerTaxRate } = taxRate as { taxRate?: string | number };

    if (innerTaxRate == null || innerTaxRate === '') {
      return '';
    }

    return Number(innerTaxRate);
  }

  if (taxRate == null || taxRate === '') {
    return '';
  }

  return Number(taxRate);
}

export function useCreateSubmitHandler({
  record,
  menuId,
  form,
  editItemDrawerHandlers,
  updateCubohMenu,
  itemHasPrices,
  itemHasClassifications,
  selectedParents,
  selectedChildren,
  setPosLock,
  setTaxRateEnabled,
  handleImageUpload,
  onSuccess,
  isPosSyncLocked = false,
}: CreateSubmitHandlerParams): () => void {
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();

  const onSubmit = async (
    values: Omit<EditDrawerFormValues, 'isDefault'> & { isDefault?: boolean },
  ): Promise<void> => {
    setTaxRateEnabled(true);
    const { companyId, itemType } = record;
    try {
      let newImageUrl = '';

      if (values.uploadImageFile) {
        newImageUrl = await handleImageUpload({
          companyId,
          itemType,
          uploadImageFile: values.uploadImageFile,
        });
      }

      const imageUrl = newImageUrl || values.imageUrl || '';

      const originalClassifications = record.classifications || [];

      const currentClassificationsMap = form.values.classifications.map(c => c.itemClass);

      // Marks existing classifications as isDeleted: true if they're no longer selected
      const updatedClassifications = originalClassifications.map(classification => {
        const isStillSelected = currentClassificationsMap.includes(classification.itemClass);
        return isStillSelected ? classification : { ...classification, isDeleted: true };
      });

      form.values.classifications.forEach(classification => {
        const existingClassification = originalClassifications.find(
          c => c.itemClass === classification.itemClass,
        );
        if (!existingClassification) {
          updatedClassifications.push(classification);
        }
      });

      /* OPEN QUESTION (CN-50652 / CN-49731): this PATCHes the whole record, POS-owned fields
      included, with no changed-field diffing. If the backend derives field-level user overrides
      from what the payload contains, a description-only save on a POS-synced item would mark
      every field as overridden and freeze it against future syncs. Confirm with the CN-49731
      owner before production; if it does, this needs to send only the edited keys. */
      const updatedRecord: Record<string, unknown> = {
        ...record,
        ...values,
        imageUrl,
        parents: selectedParents,
        children: selectedChildren,
        minimum: form.values.minimum,
        maximum: form.values.maximum,
        individualMinimum: form.values.individualMinimum,
        individualMaximum: form.values.individualMaximum,
        secondaryPrice: form.values.secondaryPrice || null,
        taxRate: getTaxRate(form.values.taxRate),
        servingsTag: normalizeServingsTag(form.values.servingsTag),
        ...(itemHasPrices && {
          // A POS-locked drawer skips validatePrice, so a POS record with no price reaches
          // here as '' - send null like secondaryPrice rather than an empty string.
          price: form.values.price === '' ? null : form.values.price,
        }),
        ...(itemHasClassifications && {
          classifications: form.values.classifications,
        }),
        ...(record?.itemType === MENU_OBJECT_TYPES.modifiers && {
          isDefault: form.values.isDefault === 'true',
        }),
      };

      /* The POS-locked drawer's parent/child pickers are read-only, so there is no user intent
      to submit here - and `selectedParents` is [] whenever the record wasn't hydrated with its
      relationships. Sending that empty list would read as "detach from every parent", so ask
      updateCubohMenu to leave the keys out of the PATCH altogether. */
      await updateCubohMenu({
        group: record?.itemType,
        id: record.key,
        data: updatedRecord,
        omitRelationships: isPosSyncLocked,
      });

      // Canonicalize the tracked fields so fields_changed reflects real edits. `record`
      // holds raw API values (possibly undefined, object-shaped taxRate, numeric strings)
      // while updatedRecord has form transforms applied; normalizing both the same way
      // avoids false positives (taxRate shape, secondaryPrice 0→null, '' vs undefined,
      // number vs numeric string).
      // NOTE: the returned keys must stay in sync with EDIT_DRAWER_TRACKED_FIELDS — a field
      // present in one but not the other is silently never reported as changed.
      const normalizeForDiff = (rec: Record<string, unknown>): Record<string, unknown> => {
        const taxRate = getTaxRate(rec.taxRate as Parameters<typeof getTaxRate>[0]);
        const { price } = rec;
        return {
          name: rec.name || '',
          description: rec.description || '',
          enabled: !!rec.enabled,
          classifications: rec.classifications ?? [],
          taxRate: taxRate === '' ? null : taxRate,
          secondaryPrice: rec.secondaryPrice || null,
          price: price === undefined || price === null || price === '' ? null : Number(price),
        };
      };

      trackMenuEntityAction({
        action: 'update',
        entityType: toAnalyticsEntityType(record?.itemType),
        menuId,
        entityId: record?.uuid,
        fieldsChanged: diffChangedFields(
          normalizeForDiff(record),
          normalizeForDiff(updatedRecord),
          EDIT_DRAWER_TRACKED_FIELDS,
        ),
      });

      form.setFieldValue('imageUrl', imageUrl);

      const readableItemType = getReadableItemType(record?.itemType);

      handleSuccessNotification({
        readableItemType,
        parentUuid: record?.parentUuid,
        extraProps: {
          id: record?.uuid,
          itemType: record?.itemType,
        },
      });

      editItemDrawerHandlers.close();
      onSuccess?.();
    } catch (e) {
      const readableItemType = getReadableItemType(record?.itemType);
      handleErrorNotification({ readableItemType });
    } finally {
      setPosLock(true);
    }
  };

  return (): void => {
    const validation = form.validate();

    /* On a POS-locked drawer the parent/child pickers are read-only, so a POS menu that
    already violates these rules would block a description save the user can't unblock. */
    const requiresParent =
      !isPosSyncLocked &&
      record?.itemType != null &&
      [
        MENU_OBJECT_TYPES.items,
        MENU_OBJECT_TYPES.modifiers,
        MENU_OBJECT_TYPES.modifierGroups,
      ].includes(record.itemType);
    const hasActiveParent = selectedParents.some(p => !p.isDeleted);
    const hasParentError = requiresParent && !hasActiveParent;

    /* Modifier groups must retain at least one modifier — the backend returns a
    500 on empty children (BE bug; remove this guard once that's fixed). Deliberately NOT
    relaxed for POS-locked drawers: mod groups never open one (isPosSyncEditableType excludes
    them), and if that changes, an inline "Modifier is required." beats that 500. */
    const hasActiveChild = selectedChildren.some(c => !c.isDeleted);
    const hasChildError = record?.itemType === MENU_OBJECT_TYPES.modifierGroups && !hasActiveChild;

    // setErrors with a callback ensures the error merges after validate()'s clear in the same batch
    if (hasParentError) {
      const parentRequiredMessage = getParentRequiredMessage(record?.itemType, !!record?.isNested);
      form.setErrors(current => ({ ...current, parents: parentRequiredMessage }));
    }

    if (hasChildError) {
      form.setErrors(current => ({ ...current, children: 'Modifier is required.' }));
    }

    if (!validation.hasErrors && !hasParentError && !hasChildError) {
      const values = {
        ...form.values,
        isDefault: form.values.isDefault === 'true',
      };

      void onSubmit(values);
    }
  };
}
